import React, { useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Paper,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Grid,
  Card,
  CardActionArea,
  alpha,
  Alert,
} from "@mui/material";
import {
  FaBuilding,
  FaInfoCircle,
  FaPhone,
  FaGlobe,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaLink,
  FaCheckCircle,
  FaGraduationCap,
  FaPlane,
  FaHeartbeat,
  FaHome,
  FaRunning,
  FaEllipsisH,
  FaBriefcase,
  FaClipboardCheck,
  FaIndustry,
  FaSignature,
  FaComment,
  FaTimesCircle,
  FaArrowLeft,
  FaPaperPlane,
} from "react-icons/fa";

import SocialContainer from "./containers/SocialContainer";
import { useNavigate, useParams } from "react-router-dom";
import useApiEp from "../hooks/useApiEp";
import LoadingWidget from "../widgets/LoadingWidget";
import { getErrorResponse, getHeader, toastError } from "../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../utils/constants";
import { toast } from "react-toastify";
const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
const steps = ["Business Details", "Company Information", "Review & Submit"];

const industrySectors = [
  {
    name: "Education",
    icon: <FaGraduationCap size={24} />,
    gradient: "linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)",
  },
  {
    name: "Travel",
    icon: <FaPlane size={24} />,
    gradient: "linear-gradient(135deg, #26c6da 0%, #0097a7 100%)",
  },
  {
    name: "Medical",
    icon: <FaHeartbeat size={24} />,
    gradient: "linear-gradient(135deg, #ef5350 0%, #d32f2f 100%)",
  },
  {
    name: "Real Estate",
    icon: <FaHome size={24} />,
    gradient: "linear-gradient(135deg, #66bb6a 0%, #388e3c 100%)",
  },
  {
    name: "Sports",
    icon: <FaRunning size={24} />,
    gradient: "linear-gradient(135deg, #ffa726 0%, #f57c00 100%)",
  },
  {
    name: "Others",
    icon: <FaEllipsisH size={24} />,
    gradient: "linear-gradient(135deg, #bdbdbd 0%, #757575 100%)",
  },
];

const hearAboutOptions = [
  "Google Search",
  "Social Media",
  "Referral",
  "Advertisement",
  "Other",
];

// Social media logo URLs from Wikimedia
const SelfOnBoarding = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useApiEp(
    `/api/check-email-verification?id=${id}`
  );
  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1
    companyName: "",
    website: "",
    address: "",
    industrySector: "",

    // Step 2
    companyShortName: "",
    contactNumber: "",
    hearAbout: "",
    usedWhatsappApi: false,
  });

  const handleNext = () => {
    if (activeStep === 0) {
      const errors = {};
      const trimmedFormData = {
        companyName: formData.companyName.trim(),
        address: formData.address.trim(),
        website: formData.website.trim(),
        industrySector: formData.industrySector.trim(),
      };
      // Validate required fields
      if (!trimmedFormData.companyName) {
        errors.companyName = "Company name is required";
      }
      if (!urlRegex.test(trimmedFormData.website)) {
        errors.website = "Please enter a valid URL";
      }
      if (!trimmedFormData.address) {
        errors.address = "Address is required";
      }
      if (!trimmedFormData.industrySector) {
        errors.industrySector = "Industry sector is required";
      }
      if (Object.keys(errors).length > 0) {
        alert(
          "Please fill the required fields:\n\n" +
            Object.values(errors).join("\n")
        );
        return;
      }
      setActiveStep((prevStep) => prevStep + 1);
    } else if (activeStep === 1) {
      const errors = {};
      // Trim strings to avoid whitespace issues
      const trimmedFormData = {
        companyShortName: formData.companyShortName.trim(),
        contactNumber: formData.contactNumber.trim(),
        hearAbout: formData.hearAbout.trim(),
      };
      if (!trimmedFormData.companyShortName) {
        errors.companyShortName = "Company short name is required";
      }
      if (!trimmedFormData.contactNumber) {
        errors.contactNumber = "Contact number is required";
      }
      if (!trimmedFormData.hearAbout) {
        errors.contactNumber = "Hear about is required";
      }
      if (typeof formData.usedWhatsappApi !== "boolean") {
        errors.usedWhatsappApi = "Please specify if you have used WhatsApp API";
      }
      if (Object.keys(errors).length > 0) {
        alert(
          "Please fill the required fields:\n\n" +
            Object.values(errors).join("\n")
        );
        return;
      }
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSectorSelect = (sector) => {
    setFormData((prev) => ({
      ...prev,
      industrySector: sector,
    }));
  };

  const handleSubmit = async () => {
    // Validation object to store errors
    const errors = {};

    // Trim strings to avoid whitespace issues
    const trimmedFormData = {
      companyName: formData.companyName.trim(),
      website: formData.website.trim(),
      address: formData.address.trim(),
      industrySector: formData.industrySector.trim(),
      companyShortName: formData.companyShortName.trim(),
      contactNumber: formData.contactNumber.replace(/\s/g, ""),
      hearAbout: formData.hearAbout.trim(),
      usedWhatsappApi: formData.usedWhatsappApi,
    };

    // Validate required fields
    if (!trimmedFormData.companyName) {
      errors.companyName = "Company name is required";
    }
    if (!urlRegex.test(trimmedFormData.website)) {
      errors.website = "Please enter a valid URL";
    }
    if (!trimmedFormData.address) {
      errors.address = "Address is required";
    }
    if (!trimmedFormData.industrySector) {
      errors.industrySector = "Industry sector is required";
    }
    if (!trimmedFormData.companyShortName) {
      errors.companyShortName = "Company short name is required";
    }
    if (!trimmedFormData.contactNumber) {
      errors.contactNumber = "Contact number is required";
    }
    // Validate usedWhatsappApi (ensure it's a boolean)
    if (typeof trimmedFormData.usedWhatsappApi !== "boolean") {
      errors.usedWhatsappApi = "Please specify if you have used WhatsApp API";
    }

    // Optional: Add specific validation for contactNumber (e.g., simple regex for phone number)
    const phoneRegex = /^[0-9]{10}$/; // Example: 10-digit phone number
    if (
      trimmedFormData.contactNumber &&
      !phoneRegex.test(trimmedFormData.contactNumber)
    ) {
      console.log(trimmedFormData.contactNumber);
      errors.contactNumber = "Please enter a valid 10-digit contact number";
    }
    if (!urlRegex.test(trimmedFormData.website)) {
      errors.website = "Please enter a valid URL";
    }
    // Check if there are any errors
    if (Object.keys(errors).length > 0) {
      // Display errors (you can customize how to show errors, e.g., alert or UI feedback)
      alert(
        "Please fix the following errors:\n" + Object.values(errors).join("\n")
      );
      console.log("Validation errors:", errors);
      return;
    }

    try {
      setSubmitting(false);
      const response = await axios.post(
        `${API_ENDPOINT}/api/customer-registration?id=${id}`,
        formData
      );
      const responseD = response.data;
      toast.success(responseD.message);
      if (responseD?.registered) {
        navigate(`/login`);
      } else {
        const pwdToken = responseD.pwdToken;
        navigate(`/set-password/${pwdToken}`);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepOne = () => (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <FaBuilding style={{ marginRight: 10, color: "#1976d2" }} />
        <Typography variant="h6" color="primary" fontWeight="600">
          Business Information
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <FaBriefcase
          style={{
            color: "#666",
            marginTop: 16,
            marginRight: 16,
            flexShrink: 0,
          }}
        />
        <TextField
          size="small"
          fullWidth
          label="Company Name"
          value={formData.companyName}
          onChange={(e) => handleInputChange("companyName", e.target.value)}
          sx={{ mb: 2.5 }}
          required
          variant="outlined"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2.5 }}>
        <FaGlobe
          style={{
            color: "#666",
            marginTop: 16,
            marginRight: 16,
            flexShrink: 0,
          }}
        />
        <TextField
          fullWidth
          size="small"
          label="Website"
          required
          helperText="Required for blue tick approval"
          placeholder="https://example.com"
          value={formData.website}
          onChange={(e) => handleInputChange("website", e.target.value)}
          variant="outlined"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
        <FaMapMarkerAlt
          style={{
            color: "#666",
            marginTop: 16,
            marginRight: 16,
            flexShrink: 0,
          }}
        />
        <TextField
          fullWidth
          size="small"
          label="Address"
          multiline
          rows={3}
          value={formData.address}
          onChange={(e) => handleInputChange("address", e.target.value)}
          required
          variant="outlined"
        />
      </Box>

      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{ mb: 2, fontWeight: "600" }}
      >
        Industry Sector *
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {industrySectors.map((sector) => (
          <Grid item xs={6} sm={4} key={sector.name}>
            <Card
              sx={{
                height: "100%",
                background:
                  formData.industrySector === sector.name
                    ? sector.gradient
                    : "transparent",
                border:
                  formData.industrySector === sector.name
                    ? "none"
                    : "2px solid #e0e0e0",
                borderRadius: 2,
                transition: "all 0.2s ease-in-out",
                boxShadow:
                  formData.industrySector === sector.name
                    ? "0 8px 16px rgba(0,0,0,0.1)"
                    : "0 4px 8px rgba(0,0,0,0.05)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                },
              }}
            >
              <CardActionArea
                onClick={() => handleSectorSelect(sector.name)}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 2.5,
                  color:
                    formData.industrySector === sector.name
                      ? "white"
                      : "text.primary",
                }}
              >
                <Box
                  sx={{
                    mb: 1.5,
                    color:
                      formData.industrySector === sector.name
                        ? "white"
                        : "primary.main",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    backgroundColor:
                      formData.industrySector === sector.name
                        ? alpha("#fff", 0.2)
                        : alpha("#1976d2", 0.1),
                  }}
                >
                  {sector.icon}
                </Box>
                <Typography variant="body2" align="center" fontWeight="500">
                  {sector.name}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderStepTwo = () => (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <FaInfoCircle style={{ marginRight: 10, color: "#1976d2" }} />
        <Typography variant="h6" color="primary" fontWeight="600">
          Additional Details
        </Typography>
      </Box>

      <TextField
        fullWidth
        inputProps={{ maxLength: 50 }}
        label="Company Short Name"
        placeholder="e.g., ACME Corp"
        value={formData.companyShortName}
        onChange={(e) => {
          const value = e.target.value;
          if (value.length <= 50 && /^[a-zA-Z0-9 _]*$/.test(value)) {
            handleInputChange("companyShortName", value);
          }
        }}
        sx={{ mb: 2.5 }}
        required
        helperText="This will be your company id in out system. max length: 50"
        variant="outlined"
      />

      <TextField
        fullWidth
        label="Contact Number"
        placeholder="+91 (5676) 614567"
        value={formData.contactNumber}
        onChange={(e) => handleInputChange("contactNumber", e.target.value)}
        sx={{ mb: 2.5 }}
        required
        variant="outlined"
      />

      <FormControl required fullWidth sx={{ mb: 2.5 }} variant="outlined">
        <InputLabel>How did you hear about Info Canny?</InputLabel>
        <Select
          value={formData.hearAbout}
          label="How did you hear about Info Canny?"
          onChange={(e) => handleInputChange("hearAbout", e.target.value)}
        >
          {hearAboutOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={formData.usedWhatsappApi}
            onChange={(e) =>
              handleInputChange("usedWhatsappApi", e.target.checked)
            }
            icon={<FaWhatsapp style={{ color: "#25D366", fontSize: "24px" }} />}
            checkedIcon={
              <div style={{ position: "relative", display: "inline-block" }}>
                <FaWhatsapp style={{ color: "#25D366", fontSize: "24px" }} />
                <FaCheckCircle
                  style={{
                    color: "#25D366",
                    fontSize: "12px",
                    position: "absolute",
                    bottom: "-4px",
                    right: "-4px",
                  }}
                />
              </div>
            }
          />
        }
        label="Have you used WhatsApp Business API earlier?"
        sx={{ mb: 2 }}
      />
    </Box>
  );
  const renderStepThree = () => (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <FaClipboardCheck
          color="#1976d2"
          size={40}
          style={{ marginBottom: "8px" }}
        />
        <Typography variant="h5" gutterBottom>
          Review Your Information
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please verify all details before submitting
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <FaBuilding color="#1976d2" style={{ marginRight: "8px" }} />
          <Typography variant="h6" sx={{ color: "primary.main" }}>
            Company Information
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FaSignature
                size={16}
                style={{ marginRight: "8px", color: "textSecondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                Company Name
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom sx={{ ml: 3 }}>
              {formData.companyName || "Not provided"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FaGlobe
                size={16}
                style={{ marginRight: "8px", color: "textSecondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                Website
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom sx={{ ml: 3 }}>
              {formData.website || "Not provided"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FaMapMarkerAlt
                size={16}
                style={{ marginRight: "8px", color: "textSecondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                Address
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom sx={{ ml: 3 }}>
              {formData.address || "Not provided"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FaIndustry
                size={16}
                style={{ marginRight: "8px", color: "textSecondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                Industry Sector
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom sx={{ ml: 3 }}>
              {formData.industrySector || "Not provided"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <FaPhone color="#1976d2" style={{ marginRight: "8px" }} />
          <Typography variant="h6" sx={{ color: "primary.main" }}>
            Contact Details
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FaSignature
                size={16}
                style={{ marginRight: "8px", color: "textSecondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                Company Short Name
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom sx={{ ml: 3 }}>
              {formData.companyShortName || "Not provided"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FaPhone
                size={16}
                style={{ marginRight: "8px", color: "textSecondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                Contact Number
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom sx={{ ml: 3 }}>
              {formData.contactNumber || "Not provided"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FaComment
                size={16}
                style={{ marginRight: "8px", color: "textSecondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                How did you hear about us?
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom sx={{ ml: 3 }}>
              {formData.hearAbout || "Not provided"}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FaWhatsapp
                size={16}
                style={{ marginRight: "8px", color: "textSecondary" }}
              />
              <Typography variant="body2" color="text.secondary">
                Used WhatsApp API before?
              </Typography>
            </Box>
            <Typography variant="body1" gutterBottom sx={{ ml: 3 }}>
              {formData.usedWhatsappApi ? (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FaCheckCircle
                    color="success"
                    size={16}
                    style={{ marginRight: "4px" }}
                  />
                  Yes
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FaTimesCircle
                    color="error"
                    size={16}
                    style={{ marginRight: "4px" }}
                  />
                  No
                </Box>
              )}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<FaArrowLeft />}
          sx={{ minWidth: 120 }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          startIcon={<FaPaperPlane />}
          sx={{ minWidth: 120 }}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );

  if (isLoading) {
    return <LoadingWidget label="Checking link...." />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }
  return (
    <SocialContainer>
      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={8}
          sx={{
            p: 4,
            borderRadius: 3,
            background: "white",
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(255, 255, 255, 0.95)",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <FaWhatsapp
                style={{
                  color: "#25D366",
                  fontSize: "2.5rem",
                  marginRight: "10px",
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "700",
                  background:
                    "linear-gradient(135deg, #1976d2 0%, #25D366 100%)",
                  backgroundClip: "text",
                  textFillColor: "transparent",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                WhatsApp Business API
              </Typography>
            </Box>
            <Typography variant="body1" color="textSecondary">
              Connect with your customers at scale
            </Typography>
          </Box>

          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{
              fontWeight: "700",
              mb: 4,
              color: "primary.main",
            }}
          >
            Business Registration
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontWeight: "500",
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 2 }}>
            {activeStep === 0 && renderStepOne()}
            {activeStep === 1 && renderStepTwo()}
            {activeStep === 2 && renderStepThree()}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              variant="outlined"
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
                fontWeight: "500",
              }}
            >
              Back
            </Button>

            {activeStep < steps.length - 1 && (
              <Button
                onClick={handleNext}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1,
                  fontWeight: "500",
                  background:
                    "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #1565c0 0%, #1976d2 100%)",
                  },
                }}
              >
                Next
              </Button>
            )}
          </Box>

          {activeStep === 2 && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Chip
                icon={<FaCheckCircle />}
                label="All steps completed"
                color="success"
                variant="outlined"
                sx={{
                  fontWeight: "500",
                  py: 1.5,
                }}
              />
            </Box>
          )}
        </Paper>

        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Typography variant="body2" color="textSecondary">
            © 2025 Canny InfoTech. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </SocialContainer>
  );
};

export default SelfOnBoarding;
